import React, { useCallback, useEffect, useState } from 'react';
import type { CallList, MailersHeader, MailersTemplate } from 'bundles/connect/types';
import { Card } from 'react-bootstrap-5';
import MailersTemplatesHeader from './mailers_templates_header';
import MailersTemplatesTable from './mailers_templates_table';
import MailersTemplatesTableHeader from './mailers_templates_table_header';
import { fetchMailersTemplates } from 'bundles/connect/helpers/prospect_api';
import { debounce } from 'lodash';

type MailersTempaltesIndexProps = {
  callList: CallList;
}

const TITLE_QUERY_KEY = 'q[title_cont]';

export default function MailersTemplatesIndex({ callList }: MailersTempaltesIndexProps) {
  const [templates, setTemplates] = useState<MailersTemplate[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [header, setHeader] = useState<MailersHeader>({ htmlLeft: '', htmlRight: '', logoUrl: '' });
  const [selectedTemplates, setSelectedTemplates] = useState<number[]>([]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    setLoading(true);
    fetchMailersTemplates({ page: currentPage }, callList.id).then((r) => {
      setTemplates(r.templates);
      setHeader(r.header);
      setTotal(r.total);
    }).finally(() => setLoading(false));
  }, []);

  const debouncedSearch = useCallback(
    debounce((query) => {
      search(query);
    }, 300),
    []
  );

  const search = (query: string) => {
    setLoading(true);
    setCurrentPage(1);
    const params = {
      [TITLE_QUERY_KEY]: query
    };
    fetchMailersTemplates(params, callList.id).then((data) => {
      setTemplates(data.templates);
      setTotal(data.total);
    }).finally(() => setLoading(false));
  };

  const handleSearchTextChange = (text: string) => {
    setSearchText(text);
    debouncedSearch(text);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setLoading(true);
    fetchMailersTemplates({ page, [TITLE_QUERY_KEY]: searchText }, callList.id).then((data) => {
      setTemplates(data.templates);
    }).finally(() => setLoading(false));
  };

  return (
    <div className="position-absolute w-100 h-100 d-flex flex-column">
      <div className="d-flex flex-column flex-grow-1 overflow-hidden">
        <MailersTemplatesHeader callListId={callList.id} />
        <Card className="mb-4 d-flex flex-column p-4 gap-1 m-6 shadow">
          <span>Preparing mailers for list:</span>
          <h6 className="mb-0 fw-bold">{callList.name}</h6>
        </Card>
        <Card className="d-flex flex-column overflow-auto h-100 m-6 shadow">
          <Card.Header className="p-4">
            <MailersTemplatesTableHeader
              callListId={callList.id}
              header={header}
              selectedTemplates={selectedTemplates}
              setSelectedTemplates={setSelectedTemplates}
              setTemplates={setTemplates}
              total={total}
              onSearchTextChange={handleSearchTextChange}
              onUpdateTotal={(newTotal: number) => setTotal(newTotal)}
            />
          </Card.Header>
          <MailersTemplatesTable
            callListId={callList.id}
            currentPage={currentPage}
            loading={loading}
            selectedTemplates={selectedTemplates}
            setSelectedTemplates={setSelectedTemplates}
            setTemplates={setTemplates}
            templates={templates}
            total={total}
            onChangePage={handlePageChange}
          />
        </Card>
      </div>
    </div>
  );
}
